import { CONTACT_EMAIL } from "../lib/site";
import { Body, Bullets, Em, GOLD, GOLD_DEEP, IVORY, Kicker, Ornament, Rows, SLATE, Section, Serif, Title, Hero } from "../components/ms/kit";
import InquiryForm from "../components/ms/InquiryForm";
import PageShell from "../components/ms/PageShell";

const BENEFITS = [
  "Education in the fundamentals of angel investing, taught by active investors",
  "Connection with experienced investors and venture partners",
  "A seat at investor selection meetings, where members hear founders pitch and ask their own questions",
  "The chance to review and invest in vetted companies alongside other members, on your own terms",
  "Founder showcases and Demo Day",
  "Tech Nights at the Globe and gatherings in Prairie communities",
  "Long-term relationships across the Prairie ecosystem",
];

const SESSIONS = [
  ["How early-stage investing works", "The stages from pre-seed to Series A, what angels and seed funds do, and where the Prairies fit nationally."],
  ["Reading a startup's financials", "Unit economics, burn and runway, and where angel investing sits within a personal portfolio."],
  ["Term sheets", "The key terms, the protections investors need, and the common traps on both sides of the table."],
  ["Valuations, SAFEs and conversion", "How early rounds are priced, how SAFEs and convertible notes work, and what happens when they convert."],
  ["Syndicates and due diligence", "How syndicates are formed and led, how to build a diligence checklist, and how to run diligence as a group."],
  ["Building a portfolio", "Portfolio construction, deal volume, and co-investing alongside a lead."],
];

const FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email", type: "email" },
  { key: "city", label: "City and province" },
  { key: "background", label: "Your background", optional: true },
  { key: "journey", label: "Where you are on your angel journey", options: ["New to investing", "Have made a few investments", "An experienced angel"], wide: true },
  { key: "want", label: "What you would most like from the network", type: "textarea", optional: true, wide: true },
  { key: "accredited", label: "Are you an accredited investor?", options: ["Yes", "No", "Not sure"], wide: true },
];

export const Split = ({ kicker, title, children, testId }) => (
  <Section testId={testId}>
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-12 md:gap-24 items-start">
      <div className="md:sticky md:top-28">
        <p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>{kicker}</p>
        {title && <Title className="mt-6">{title}</Title>}
      </div>
      <div className="space-y-7 md:pt-12">{children}</div>
    </div>
  </Section>
);

const SectionHead = ({ kicker, children }) => (
  <div className="text-center max-w-3xl mx-auto">
    <p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>{kicker}</p>
    <Title className="mt-6">{children}</Title>
  </div>
);

export default function Angels() {
  return (
    <PageShell testId="angels-page" disclaimer>
      <Hero kicker="Maven Skies Angels" testId="angels"
        body="A curated network for people who want to learn angel investing, see quality opportunities, and build long-term relationships across the Prairie innovation ecosystem."
        extra={<><Ornament /><p className="text-[11px] tracking-[0.32em] uppercase" style={{ color: GOLD_DEEP }}>Building people · Building capital · Building the future</p></>}>
        Building the Prairie <Em>angel investor</Em> community.
      </Hero>
      <Split kicker="I · The Opportunity" title={<>The Prairies are full of <Em>potential.</Em></>} testId="opportunity">
        <Body>Many professionals, entrepreneurs, executives and business leaders across the Prairies have the financial capacity to invest in startups. Most have never had the education, the confidence, the network or the access to quality opportunities that investing requires.</Body>
        <Body tone={IVORY}>Maven Skies Angels exists to close that gap. We connect investors, founders and the wider ecosystem, and we work alongside the organizations already doing great work here instead of replacing them.</Body>
      </Split>
      <Split kicker="II · Who It Is For" title={<>Any stage of the <Em>angel journey.</Em></>} testId="who-for">
        <Body tone={IVORY}>Maven Skies Angels is for people at any stage of their angel journey. Some of our members have never written a cheque and want to understand how it works before they start. Others have invested before and want better deal flow, a trusted group to invest alongside, and a way to share the work of diligence.</Body>
      </Split>
      <Section testId="member-benefits" width="max-w-5xl">
        <SectionHead kicker="III · What Members Get">A community, <Em>not just</Em> a deal list.</SectionHead>
        <Bullets items={BENEFITS} testId="angels-benefits" />
      </Section>
      <Split kicker="IV · How Deals Reach the Network" title={<>Curated <Em>by design.</Em></>} testId="deal-flow">
        <Body>Founders can apply directly and go through a structured review. Trusted members and venture partners can also refer companies, and each referral reflects the judgment and track record of the person making it.</Body>
        <Body tone={IVORY}>However an opportunity arrives, it goes through the same diligence standard before it is shared with the group. Members then make their own investment decisions.</Body>
      </Split>
      <Section id="readiness" testId="angels-fall-program" width="max-w-5xl">
        <SectionHead kicker="V · Angel Readiness">A practical path from first questions to <Em>real deal experience.</Em></SectionHead>
        <Body className="mt-8 max-w-3xl mx-auto text-center">Members move through a series of live online sessions that build from the fundamentals to the mechanics of a deal and then to thinking about a whole portfolio. Along the way, you will see how the process works in practice.</Body>
        <Rows items={SESSIONS} testId="angels-sessions" labelWidth="md:grid-cols-[300px_1fr]" />
        <div className="mt-12 max-w-3xl mx-auto space-y-6">
          <Body>The sessions are followed by investor selection meetings, where members hear from founders and decide together what to look at more closely. The program closes with Demo Day. You will also be welcome at panels on what angel groups look for, going global, and raising, scaling and exiting, as well as at Tech Nights at the Globe. We deliver the investor education alongside Venture University.</Body>
          <Body tone={IVORY}>By the end, a new angel has real deal experience and is ready to sit on a selection committee. An experienced angel has a sharper process and a group to invest alongside.</Body>
        </div>
      </Section>
      <Section id="join" testId="angels-join" className="py-32 md:py-44" width="max-w-4xl">
        <div className="text-center">
          <Kicker>VI · Express Your Interest</Kicker>
          <p className="mt-10 leading-[1.25] tracking-[-0.01em]" style={{ ...Serif, color: IVORY, fontSize: "clamp(1.85rem, 3.6vw, 2.75rem)", fontStyle: "italic" }}>
            Investing starts with a community that knows what it is looking for.
          </p>
          <Body className="mt-8 max-w-2xl mx-auto">Maven Skies Angels is where Prairie professionals become confident, connected investors. Membership is by invitation, and we read every request personally. Tell us a little about yourself and we will be in touch about the next steps.</Body>
        </div>
        <InquiryForm form="angels" fields={FIELDS} submitLabel="Request Invitation" testId="angels-form"
          thanks="Your request has reached us. We read every request personally and will be in touch about the next steps." />
        <p className="mt-10 text-center text-[14px]" style={{ color: SLATE }}>
          Questions first? Write to Danielle at <a href={`mailto:${CONTACT_EMAIL}`} className="underline underline-offset-4" style={{ color: GOLD }} data-testid="angels-email">{CONTACT_EMAIL}</a>.
        </p>
      </Section>
    </PageShell>
  );
}
